import type { Contexte, Lecture, Ligne, Mutation, Op, Table } from './types.ts'
import { appliquerOp } from './index.ts'
import { TABLES_SYNCHRONISEES } from '../../../db/schema.ts'

/**
 * Adaptateur MÉMOIRE de la couche d'opérations.
 *
 * C'est la moitié client du contrat : le miroir IndexedDB est chargé ici au
 * démarrage, et l'affichage se calcule en repliant l'outbox par-dessus.
 * Le serveur a son propre adaptateur, adossé à SQLite, et les deux DOIVENT
 * produire le même état final — c'est exactement ce que vérifie le banc de
 * rejeu.
 */

export type EtatMemoire = Record<Table, Map<string, Ligne>>

export function etatVide(): EtatMemoire {
  const e = {} as EtatMemoire
  for (const t of TABLES_SYNCHRONISEES) e[t] = new Map()
  return e
}

export function cloner(etat: EtatMemoire): EtatMemoire {
  const e = {} as EtatMemoire
  for (const t of TABLES_SYNCHRONISEES) e[t] = new Map(etat[t])
  return e
}

export function lecture(etat: EtatMemoire): Lecture {
  return {
    get: (table, id) => etat[table].get(id),
    tous: (table) => [...etat[table].values()],
  }
}

/**
 * Les index UNIQUES du schéma, rejoués ici.
 *
 * Le moteur mémoire n'avait aucune contrainte, et c'est ce qui a laissé
 * passer DEUX bugs jusqu'en production : reprendre le code-barres d'un
 * produit fusionné, et recréer une ligne d'inventaire supprimée. Dans les
 * deux cas SQLite refusait — « UNIQUE constraint failed » — mais les tests
 * étaient verts, parce qu'ils ne font tourner que ce moteur-ci.
 *
 * Un banc de rejeu qui ne reproduit pas les contraintes du stockage ne
 * vérifie qu'une moitié du système, et pas celle où vivent ces bugs-là. La
 * tombe compte : une ligne supprimée occupe toujours sa clé, puisqu'on
 * n'efface jamais physiquement.
 */
const UNICITE: Partial<Record<Table, readonly string[][]>> = {
  inventoryItems: [['catalogItemId', 'locationId']],
  catalogItems: [['seedSlug']],
}

function verifierUnicite(etat: EtatMemoire, table: Table, ligne: Ligne): void {
  for (const cle of UNICITE[table] ?? []) {
    const valeurs = cle.map((c) => ligne[c as keyof Ligne])
    if (valeurs.some((v) => v === null || v === undefined)) continue
    for (const autre of etat[table].values()) {
      if (autre.id === ligne.id) continue
      if (cle.every((c, i) => autre[c as keyof Ligne] === valeurs[i])) {
        throw new Error(
          `unicité violée sur ${table}(${cle.join(', ')}) : « ${String(autre.id)} » occupe déjà ` +
            `${valeurs.join(' / ')}${autre.deletedAt ? ' (pierre tombale — il faut la ressusciter, pas créer à côté)' : ''}`,
        )
      }
    }
  }
}

/**
 * Applique des mutations sur un état mémoire.
 *
 * `rev` est passé par l'appelant pour rester EXACTEMENT parallèle au
 * serveur, qui l'obtient du compteur du foyer. C'est ce parallélisme qui
 * rend la comparaison des deux moteurs signifiante.
 */
export function appliquerMutations(
  etat: EtatMemoire,
  mutations: readonly Mutation[],
  ctx: Contexte,
  rev: () => number,
): void {
  for (const m of mutations) {
    const table = etat[m.table]
    if (m.type === 'creer') {
      const ligne = {
        ...m.ligne,
        householdId: ctx.householdId,
        rev: rev(),
        updatedAt: ctx.maintenant,
        updatedBy: ctx.userId,
        deletedAt: null,
      }
      verifierUnicite(etat, m.table, ligne)
      table.set(m.ligne.id, ligne)
    } else if (m.type === 'modifier') {
      const ancienne = table.get(m.id)
      if (!ancienne) continue
      const nouvelle: Ligne = {
        ...ancienne,
        ...m.champs,
        rev: rev(),
        updatedAt: ctx.maintenant,
        updatedBy: ctx.userId,
      }
      // stateRev suit le changement d'état, pas toute modification de ligne :
      // c'est la granularité dont la règle de causalité a besoin.
      if ('state' in m.champs) nouvelle.stateRev = nouvelle.rev
      table.set(m.id, nouvelle)
    } else {
      const ancienne = table.get(m.id)
      if (!ancienne) continue
      // Pierre tombale, jamais d'effacement : une ligne réellement retirée
      // disparaîtrait du delta et un téléphone en veille la garderait pour
      // toujours.
      table.set(m.id, {
        ...ancienne,
        deletedAt: ctx.maintenant,
        rev: rev(),
        updatedAt: ctx.maintenant,
        updatedBy: ctx.userId,
      })
    }
  }
}

/** Applique une opération de bout en bout sur un état mémoire. */
export function jouer(etat: EtatMemoire, op: Op, ctx: Contexte, compteur: { seq: number }) {
  const resultat = appliquerOp(op, lecture(etat), { ...ctx, revCourant: compteur.seq })
  appliquerMutations(etat, resultat.mutations, ctx, () => ++compteur.seq)
  return resultat
}
